import api from '../config/api';
import { isApiEnabled } from '../config/appConfig';
import { getApiData, parseApiError } from './apiUtils';

const RESET_REQUESTS_KEY = 'blf_password_reset_requests';

const generateLocalCode = () => String(Math.floor(100000 + Math.random() * 900000));

const readLocalRequests = () => {
  try {
    const raw = window.localStorage.getItem(RESET_REQUESTS_KEY);
    const stored = raw ? JSON.parse(raw) : [];
    return Array.isArray(stored) ? stored : [];
  } catch (error) {
    return [];
  }
};

const writeLocalRequests = (requests) => {
  window.localStorage.setItem(RESET_REQUESTS_KEY, JSON.stringify(requests));
};

const getActiveLocalRequest = (email) => {
  const normalizedEmail = (email || '').trim().toLowerCase();
  const now = Date.now();

  return readLocalRequests().find((item) => {
    if (item.email !== normalizedEmail || item.usado) {
      return false;
    }

    if (item.expira_en && new Date(item.expira_en).getTime() < now) {
      return false;
    }

    return true;
  });
};

const requestPasswordResetLocal = (email) => {
  const normalizedEmail = (email || '').trim().toLowerCase();

  if (!normalizedEmail) {
    return Promise.reject(new Error('El correo electrónico es obligatorio.'));
  }

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailPattern.test(normalizedEmail)) {
    return Promise.reject(new Error('Ingresa un correo electrónico válido.'));
  }

  const codigo = generateLocalCode();
  const expiraEn = new Date(Date.now() + 15 * 60 * 1000).toISOString();
  const request = {
    id: Date.now(),
    email: normalizedEmail,
    codigo,
    codigo_verificado: false,
    intentos_fallidos: 0,
    expira_en: expiraEn,
    solicitado_en: new Date().toISOString(),
    usado: false,
  };

  try {
    const stored = readLocalRequests().filter((item) => item.email !== normalizedEmail || item.usado);
    writeLocalRequests([request, ...stored]);
  } catch (error) {
    return Promise.reject(new Error('No se pudo enviar la solicitud.'));
  }

  if (process.env.NODE_ENV === 'development') {
    // Solo para pruebas locales sin API: el código no se envía por correo.
    // eslint-disable-next-line no-console
    console.info('[Recuperación local] Código de verificación:', codigo);
  }

  return Promise.resolve({
    id: request.id,
    email: normalizedEmail,
    expira_en: expiraEn,
    usado: false,
  });
};

const verifyRecoveryCodeLocal = ({ email, codigo }) => {
  const normalizedEmail = (email || '').trim().toLowerCase();
  const normalizedCode = (codigo || '').trim();

  if (!normalizedEmail) {
    return Promise.reject(new Error('El correo electrónico es obligatorio.'));
  }

  if (!/^\d{6}$/.test(normalizedCode)) {
    return Promise.reject(new Error('El código debe tener 6 dígitos numéricos.'));
  }

  const activeRequest = getActiveLocalRequest(normalizedEmail);

  if (!activeRequest) {
    return Promise.reject(new Error('No hay una solicitud activa para este correo. Solicita un nuevo código.'));
  }

  if (activeRequest.codigo_verificado) {
    return Promise.resolve({ email: normalizedEmail });
  }

  if ((activeRequest.intentos_fallidos || 0) >= 5) {
    return Promise.reject(new Error('Se agotaron los intentos. Solicita un nuevo código.'));
  }

  if (activeRequest.codigo !== normalizedCode) {
    const updated = readLocalRequests().map((item) => {
      if (item.id !== activeRequest.id) {
        return item;
      }

      const intentos = (item.intentos_fallidos || 0) + 1;

      return {
        ...item,
        intentos_fallidos: intentos,
        usado: intentos >= 5 ? true : item.usado,
      };
    });

    writeLocalRequests(updated);

    const refreshed = updated.find((item) => item.id === activeRequest.id);
    if (refreshed && refreshed.usado) {
      return Promise.reject(new Error('Código incorrecto. Se agotaron los intentos. Solicita un nuevo código.'));
    }

    return Promise.reject(new Error('El código ingresado no es correcto.'));
  }

  const updated = readLocalRequests().map((item) =>
    item.id === activeRequest.id ? { ...item, codigo_verificado: true } : item
  );
  writeLocalRequests(updated);

  return Promise.resolve({ email: normalizedEmail });
};

const resetPasswordLocal = ({ email, password, confirmPassword }) => {
  const normalizedEmail = (email || '').trim().toLowerCase();

  if (!normalizedEmail) {
    return Promise.reject(new Error('El correo electrónico es obligatorio.'));
  }

  if (!password) {
    return Promise.reject(new Error('La contraseña es obligatoria.'));
  }

  if (password !== confirmPassword) {
    return Promise.reject(new Error('Las contraseñas no coinciden.'));
  }

  if (!isPasswordValid(password)) {
    return Promise.reject(new Error('La contraseña no cumple con los requisitos.'));
  }

  const activeRequest = getActiveLocalRequest(normalizedEmail);

  if (!activeRequest) {
    return Promise.reject(new Error('No se encontró una solicitud de recuperación para este correo.'));
  }

  if (!activeRequest.codigo_verificado) {
    return Promise.reject(new Error('Debes verificar el código antes de cambiar la contraseña.'));
  }

  try {
    const updated = readLocalRequests().map((item) =>
      item.id === activeRequest.id ? { ...item, usado: true, actualizado_en: new Date().toISOString() } : item
    );
    writeLocalRequests(updated);
  } catch (error) {
    return Promise.reject(new Error('No se pudo actualizar la contraseña.'));
  }

  return Promise.resolve({ email: normalizedEmail });
};

export const requestPasswordReset = (email) => {
  const normalizedEmail = (email || '').trim().toLowerCase();

  if (!normalizedEmail) {
    return Promise.reject(new Error('El correo electrónico es obligatorio.'));
  }

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailPattern.test(normalizedEmail)) {
    return Promise.reject(new Error('Ingresa un correo electrónico válido.'));
  }

  if (!isApiEnabled()) {
    return requestPasswordResetLocal(normalizedEmail);
  }

  return api
    .post('/auth_recuperar_solicitar.php', { email: normalizedEmail })
    .then((response) => getApiData(response, 'No se pudo enviar la solicitud.'))
    .catch((error) => {
      throw parseApiError(error);
    });
};

export const verifyRecoveryCode = ({ email, codigo }) => {
  const normalizedEmail = (email || '').trim().toLowerCase();
  const normalizedCode = (codigo || '').trim();

  if (!normalizedEmail) {
    return Promise.reject(new Error('El correo electrónico es obligatorio.'));
  }

  if (!/^\d{6}$/.test(normalizedCode)) {
    return Promise.reject(new Error('El código debe tener 6 dígitos numéricos.'));
  }

  if (!isApiEnabled()) {
    return verifyRecoveryCodeLocal({ email: normalizedEmail, codigo: normalizedCode });
  }

  return api
    .post('/auth_recuperar_verificar_codigo.php', {
      email: normalizedEmail,
      codigo: normalizedCode,
    })
    .then((response) => getApiData(response, 'No se pudo verificar el código.'))
    .catch((error) => {
      throw parseApiError(error);
    });
};

export const getPasswordRules = (password) => {
  const value = password || '';
  return {
    minLength: value.length >= 12,
    mixedCase: /[a-z]/.test(value) && /[A-Z]/.test(value),
    numbers: /\d/.test(value),
    symbols: /[^A-Za-z0-9]/.test(value),
  };
};

export const isPasswordValid = (password) => {
  const rules = getPasswordRules(password);
  return rules.minLength && rules.mixedCase && rules.numbers && rules.symbols;
};

export const resetPassword = ({ email, password, confirmPassword }) => {
  const normalizedEmail = (email || '').trim().toLowerCase();

  if (!normalizedEmail) {
    return Promise.reject(new Error('El correo electrónico es obligatorio.'));
  }

  if (!password) {
    return Promise.reject(new Error('La contraseña es obligatoria.'));
  }

  if (password !== confirmPassword) {
    return Promise.reject(new Error('Las contraseñas no coinciden.'));
  }

  if (!isPasswordValid(password)) {
    return Promise.reject(new Error('La contraseña no cumple con los requisitos.'));
  }

  if (!isApiEnabled()) {
    return resetPasswordLocal({ email: normalizedEmail, password, confirmPassword });
  }

  return api
    .post('/auth_recuperar_restablecer.php', {
      email: normalizedEmail,
      password,
      confirm_password: confirmPassword,
    })
    .then((response) => getApiData(response, 'No se pudo actualizar la contraseña.'))
    .catch((error) => {
      throw parseApiError(error);
    });
};
